import { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Film } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

export default function MediaUploader({ images = [], video = "", onImages, onVideo }) {
  const imgInput = useRef(null);
  const vidInput = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const results = (await Promise.all(files.map(upload)))
        .filter((d) => d.kind === "image")
        .map((d) => abs(d.url));
      onImages([...images, ...results]);
      toast.success(`${results.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (imgInput.current) imgInput.current.value = ""; }
  };

  const handleVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const d = await upload(file);
      onVideo(abs(d.url));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (vidInput.current) vidInput.current.value = ""; }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {images.map((im, i) => (
          <div key={i} className="relative w-20 h-24 rounded-sm overflow-hidden bg-surface group" data-testid={`uploaded-image-${i}`}>
            <img src={im} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onImages(images.filter((_, x) => x !== i))}
              className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-ink/70 text-sand opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => imgInput.current?.click()} disabled={busy}
          className="w-20 h-24 rounded-sm border-2 border-dashed border-line hover:border-clay grid place-items-center text-mute hover:text-clay transition-colors" data-testid="upload-image-btn">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
        </button>
        <input ref={imgInput} type="file" accept="image/*" multiple hidden onChange={handleImages} data-testid="image-file-input" />
      </div>

      <div className="flex items-center gap-3">
        {video ? (
          <div className="relative w-32 h-20 rounded-sm overflow-hidden bg-ink">
            <video src={video} className="w-full h-full object-cover" muted />
            <button type="button" onClick={() => onVideo("")} className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-ink/70 text-sand" data-testid="remove-video-btn"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => vidInput.current?.click()} disabled={busy}
            className="flex items-center gap-2 text-sm border border-line rounded-full px-4 py-2 hover:border-clay transition-colors" data-testid="upload-video-btn">
            <Film className="w-4 h-4" /> Add short video (optional)
          </button>
        )}
        <input ref={vidInput} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={handleVideo} data-testid="video-file-input" />
      </div>
    </div>
  );
}
